import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { RedirectIfAuthenticated, RequireAdmin, RequireAuth } from '@/app/guards';
import { AppShell } from '@/components/layout/AppShell';
import { Spinner } from '@/components/ui/spinner';
import LoginPage from '@/routes/auth/LoginPage';
import HomePage from '@/routes/home/HomePage';
import NotFoundPage from '@/routes/NotFoundPage';
import ProfilePage from '@/routes/settings/ProfilePage';

/**
 * The route table (05 §1.2) — the single source of the path → file map.
 *
 * Foundation pages (`/login`, `/`, `/settings`, the catch-all) are imported
 * eagerly: they are the first paint and they carry no heavy dependency. Every
 * feature page is a `React.lazy` chunk so the login/home bundle stays small
 * (05 §6.6) — each of those files is owned by its feature agent and exports a
 * default component at exactly the path below.
 *
 * Guards: `/login` is public, everything else sits behind `RequireAuth`, and
 * `/admin/*` additionally behind `RequireAdmin`.
 *
 * One route sits deliberately outside `AppShell`: `/sections/:sectionId/live`,
 * the screen a TA projects in the hall. It still passes `RequireAuth` — a
 * projector holds a staff session like any other tab — but a sidebar and a top
 * bar around a QR would cost the code the third of the viewport that makes it
 * readable from the back row (05 §1.2, §2.8).
 */

const CohortsPage = lazy(() => import('@/routes/cohorts/CohortsPage'));
const CohortStudentsPage = lazy(() => import('@/routes/cohorts/CohortStudentsPage'));
const StudentImportPage = lazy(() => import('@/routes/cohorts/StudentImportPage'));
const CoursesPage = lazy(() => import('@/routes/courses/CoursesPage'));
const CourseDetailPage = lazy(() => import('@/routes/courses/CourseDetailPage'));
const GradingPolicyPage = lazy(() => import('@/routes/courses/GradingPolicyPage'));
const CourseGradesPage = lazy(() => import('@/routes/courses/CourseGradesPage'));
const CourseReportPage = lazy(() => import('@/routes/courses/CourseReportPage'));
const StudentDetailPage = lazy(() => import('@/routes/students/StudentDetailPage'));
const SectionFormPage = lazy(() => import('@/routes/sections/SectionFormPage'));
const SectionDetailPage = lazy(() => import('@/routes/sections/SectionDetailPage'));
const SectionReportPage = lazy(() => import('@/routes/sections/SectionReportPage'));
const LiveAttendancePage = lazy(() => import('@/routes/sections/LiveAttendancePage'));
const HallsPage = lazy(() => import('@/routes/halls/HallsPage'));
const AdminSettingsPage = lazy(() => import('@/routes/settings/AdminSettingsPage'));
const AdminOverviewPage = lazy(() => import('@/routes/admin/AdminOverviewPage'));
const AuditLogPage = lazy(() => import('@/routes/admin/AuditLogPage'));
const GuideViewsPage = lazy(() => import('@/routes/admin/GuideViewsPage'));

/** Shown while a lazy route chunk is in flight. */
export function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-[40vh] place-items-center" data-testid="route-fallback">
      <Spinner label={t('common.loading')} />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <LoginPage />
            </RedirectIfAuthenticated>
          }
        />

        {/*
          Full-viewport, no shell. Declared BEFORE the shell branch so the
          `sections/:sectionId` routes inside it never claim this path first.
        */}
        <Route
          path="/sections/:sectionId/live"
          element={
            <RequireAuth>
              <LiveAttendancePage />
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="settings" element={<ProfilePage />} />

          <Route path="cohorts" element={<CohortsPage />} />
          <Route path="cohorts/:cohortId" element={<CohortStudentsPage />} />
          <Route path="cohorts/:cohortId/import" element={<StudentImportPage />} />

          <Route path="courses" element={<CoursesPage />} />
          <Route path="courses/:courseId" element={<CourseDetailPage />} />
          <Route path="courses/:courseId/grading" element={<GradingPolicyPage />} />
          <Route path="courses/:courseId/grades" element={<CourseGradesPage />} />
          <Route path="courses/:courseId/report" element={<CourseReportPage />} />
          <Route path="courses/:courseId/sections/new" element={<SectionFormPage />} />

          <Route path="students/:studentId" element={<StudentDetailPage />} />

          <Route path="sections/:sectionId" element={<SectionDetailPage />} />
          <Route path="sections/:sectionId/edit" element={<SectionFormPage />} />
          {/* Reached from the CLOSED branch of the section page, not the TopBar. */}
          <Route path="sections/:sectionId/report" element={<SectionReportPage />} />

          {/*
            The observability surface. `admin` is declared with `index` rather
            than as a bare path so `/admin` and `/admin/audit` are siblings
            under one guard — a TA who follows a colleague's link to any of
            them gets the same honest "this screen is for admins" page, and the
            API refuses them regardless.
          */}
          <Route
            path="admin"
            element={
              <RequireAdmin>
                <AdminOverviewPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/audit"
            element={
              <RequireAdmin>
                <AuditLogPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/guide-views"
            element={
              <RequireAdmin>
                <GuideViewsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/halls"
            element={
              <RequireAdmin>
                <HallsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/settings"
            element={
              <RequireAdmin>
                <AdminSettingsPage />
              </RequireAdmin>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
